"use client"

import * as React from 'react'
import Link from 'next/link'
import type { ColumnDef } from '@tanstack/react-table'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { ComboboxInput } from '@open-mercato/ui/backend/inputs'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { Button } from '@open-mercato/ui/primitives/button'
import { DateTimePicker } from '@open-mercato/ui/backend/inputs/DateTimePicker'
import { loadSlotLocationComboboxOptions } from '../lib/locationSearch'

type SlotRow = {
  id: string
  locationId: string
  location?: { id: string; name: string; code?: string | null } | null
  slotType: string
  startTime?: string | null
  endTime?: string | null
  cargo?: { id: string; referenceNumber: string } | null
  status: string
}

type SlotListResponse = {
  items?: SlotRow[]
  total?: number
  totalPages?: number
}

export default function SlotsPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const [rows, setRows] = React.useState<SlotRow[]>([])
  const [locationId, setLocationId] = React.useState('')
  const [from, setFrom] = React.useState<Date | null>(null)
  const [to, setTo] = React.useState<Date | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)

  React.useEffect(() => {
    const start = new Date()
    start.setMinutes(0, 0, 0)
    const end = new Date(start)
    end.setDate(end.getDate() + 1)
    setFrom(start)
    setTo(end)
  }, [])

  const columns = React.useMemo<ColumnDef<SlotRow>[]>(() => [
    {
      accessorKey: 'location',
      header: t('slots.table.location', 'Location'),
      cell: ({ row }) => row.original.location?.name ?? '—',
    },
    {
      accessorKey: 'slotType',
      header: t('slots.table.slotType', 'Slot type'),
      cell: ({ row }) => t(`slots.types.${row.original.slotType}`, row.original.slotType),
    },
    {
      accessorKey: 'startTime',
      header: t('slots.table.startTime', 'Start time'),
      cell: ({ row }) => (row.original.startTime ? new Date(row.original.startTime).toLocaleString() : '—'),
    },
    {
      accessorKey: 'endTime',
      header: t('slots.table.endTime', 'End time'),
      cell: ({ row }) => (row.original.endTime ? new Date(row.original.endTime).toLocaleString() : '—'),
    },
    {
      accessorKey: 'cargo',
      header: t('slots.table.cargo', 'Cargo'),
      cell: ({ row }) => row.original.cargo?.referenceNumber ?? t('slots.table.noCargo', 'No cargo'),
    },
    {
      accessorKey: 'status',
      header: t('slots.table.status', 'Status'),
      cell: ({ row }) => t(`slots.status.${row.original.status}`, row.original.status),
    },
  ], [t])

  const loadSlots = React.useCallback(async () => {
    if (!locationId || !from || !to) {
      setRows([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        locationId,
        from: from!.toISOString(),
        to: to!.toISOString(),
        page: String(page),
        pageSize: '50',
      })
      const response = await apiCall<SlotListResponse>(`/api/freight/slots?${params.toString()}`)
      const payload = response.result ?? {}
      setRows(Array.isArray(payload.items) ? payload.items : [])
      setTotal(typeof payload.total === 'number' ? payload.total : 0)
      setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
    } finally {
      setIsLoading(false)
    }
  }, [from, locationId, page, to])

  const loadLocationSuggestions = React.useCallback(
    async (query?: string) =>
      loadSlotLocationComboboxOptions(query).catch(() => {
        throw new Error(t('slots.pages.list.loadError', 'Failed to load slots.'))
      }),
    [t],
  )

  React.useEffect(() => {
    void loadSlots()
  }, [loadSlots, scopeVersion])

  return (
    <Page>
      <PageBody className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('slots.pages.list.title', 'Slots')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('slots.pages.list.description', 'View and manage terminal booking windows.')}
            </p>
          </div>
          <Button asChild>
            <Link href="/backend/slots/book">{t('slots.pages.book.action', 'Book slot')}</Link>
          </Button>
        </div>

        <div className="grid gap-3 rounded-lg border bg-background p-4 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="slots-location">
              {t('slots.filters.location', 'Location')}
            </label>
            <ComboboxInput
              value={locationId}
              onChange={(nextValue) => {
                setLocationId(nextValue)
                setPage(1)
              }}
              placeholder={t('slots.filters.locationPlaceholder', 'Search location by name or code')}
              loadSuggestions={loadLocationSuggestions}
              allowCustomValues={false}
            />
            <p className="text-xs text-muted-foreground">
              {t('slots.filters.locationHelp', 'Type at least part of the location name or code.')}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="slots-from">
              {t('slots.filters.from', 'From')}
            </label>
            <DateTimePicker
              value={from}
              onChange={(date) => {
                setFrom(date)
                setPage(1)
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="slots-to">
              {t('slots.filters.to', 'To')}
            </label>
            <DateTimePicker
              value={to}
              onChange={(date) => {
                setTo(date)
                setPage(1)
              }}
            />
          </div>
        </div>

        <DataTable<SlotRow>
          title={t('slots.pages.list.tableTitle', 'Slot windows')}
          data={rows}
          columns={columns}
          isLoading={isLoading}
          pagination={{
            page,
            pageSize: 50,
            total,
            totalPages,
            onPageChange: setPage,
          }}
          rowClickActionIds={['open']}
          emptyState={(
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('slots.pages.list.empty', 'No slots found for the selected range.')}
            </p>
          )}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'open',
                  label: t('slots.actions.open', 'Open'),
                  href: `/backend/slots/${row.id}`,
                },
              ]}
            />
          )}
        />
      </PageBody>
    </Page>
  )
}
